import i18n from 'i18next'

/**
 * The catalog's canonical order: by name, then by id (ATL-968).
 *
 * Every screen renders products in whatever order these helpers hand back, and until
 * now that was the order the transport happened to use. Under Firestore that was
 * `orderBy('name')` and looked fine; under Supabase reads it was Postgres' physical row
 * order, which the merchant reported as the catalog "becoming randomly ordered".
 *
 * The backend now returns the catalog ordered, but the dict it lands in is LIVE: the
 * realtime subscription appends a newly created product with `{...prev, [id]: product}`,
 * i.e. at the END, wherever its name belongs. Firestore's onSnapshot re-emitted the
 * whole ordered collection instead, so the position was never wrong there. Sorting at
 * the point of render is what makes the order independent of both.
 *
 * Plain `<` (UTF-16 code-unit order) is deliberate: it agrees with Firestore's UTF-8
 * byte ordering for every Latin/Cyrillic/Kazakh name in the fleet, so this is a no-op
 * for orgs still reading Firestore rather than a silent re-ordering of their catalog.
 * The id tiebreak makes the order total — duplicate product names are common.
 */
export const compareProductsByName = (a, b) => {
  const aName = a?.name ?? ''
  const bName = b?.name ?? ''
  if (aName !== bName) return aName < bName ? -1 : 1
  const aId = a?.id ?? ''
  const bId = b?.id ?? ''
  if (aId === bId) return 0
  return aId < bId ? -1 : 1
}

/**
 * The order of category tabs (ATL-1892): by `order`, then by id — a TOTAL order.
 *
 * It used to be `a.order - b.order`. Under Supabase reads the backend sent the order only as
 * `displayOrder`, so both sides were `undefined`, the comparator returned NaN for every pair, and the
 * engine's sort order was whatever it happened to be — the merchant's tabs in no meaningful order.
 * The same NaN arose under Firestore for products whose category is gone (no `order` at all).
 *
 * Equal orders go to the document id, because that is what a Firestore-read till already shows:
 * the categories listener is `orderBy('order')` (AppStack.js), which breaks ties by document id, and
 * the Settings list was that delivery order, as is Atlas Market's own `orderBy('order')`
 * (atlas-market HomeScreen.js). So a Firestore-read till's Settings list is unchanged, ties included,
 * and its tabs are unchanged whenever the orders are distinct. Tied TABS were never in delivery
 * order: the old stable sort ran over the order in which each category's first product appeared in
 * the name-sorted catalogue, so their order moved with product names — they now follow Settings.
 *
 * A category whose `order` is not a finite number — absent, `null`, a string, NaN — sorts AFTER every
 * ordered one (by id among themselves). For an ABSENT order this only matters where the category is
 * in the list at all: Firestore's `orderBy('order')` drops documents without the field, so a
 * Firestore-read till never receives them, while a Supabase-read till does (display_order NULL). An
 * explicit `null` is different in Firestore — `orderBy` keeps it and sorts it FIRST — but no app
 * writer stores `order: null` (create and reorder both write the list index), so treating it as
 * absent is a deliberate choice for a value the app does not produce, not a reproduction of Firestore.
 */
const hasCategoryOrder = (order) => typeof order === 'number' && Number.isFinite(order)

const compareCategoryFields = (aOrder, aId, bOrder, bId) => {
  const aHas = hasCategoryOrder(aOrder)
  const bHas = hasCategoryOrder(bOrder)
  if (aHas !== bHas) return aHas ? -1 : 1
  if (aHas && aOrder !== bOrder) return aOrder < bOrder ? -1 : 1
  const aI = aId ?? ''
  const bI = bId ?? ''
  if (aI === bI) return 0
  return aI < bI ? -1 : 1
}

/** Comparator for category objects (`{ order, id }`) — the category list and pickers. */
export const compareProductCategories = (a, b) =>
  compareCategoryFields(a?.order, a?.id, b?.order, b?.id)

/**
 * ATL-1892 — the writes a one-step reorder of the category list needs: EVERY category in the
 * reordered list, each with its new index as `order`.
 *
 * Not only the ones whose `order` looks changed. The list's `order` values are what this till READ,
 * and a Supabase-read till reads Supabase while the write goes to Firestore (the write master) as
 * well: a row whose stale Supabase value happens to equal its new index would be skipped, and the
 * Firestore document keeps its old order — the two stores then disagree and the merchant's reorder
 * is lost on every Firestore-read till. The read store cannot vouch for the other one, so nothing
 * is skipped; a category list is a handful of rows.
 *
 * @param {Array<{order?: unknown}>} list the list as rendered (compareProductCategories order)
 * @param {number} index the row being moved
 * @param {-1|1} direction -1 = up, 1 = down
 * @returns {{list: Array, writes: Array<{category: object, order: number}>} | null} null when the
 *   move is out of range
 */
export const reorderProductCategories = (list, index, direction) => {
  const target = index + direction
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return null
  const next = [...list]
  next[target] = list[index]
  next[index] = list[target]
  return { list: next, writes: next.map((category, order) => ({ category, order })) }
}

export const getProductsList = (productsDict, activeOnly=false, store=null) => {
  let productsList = Object.values(productsDict)
  if (activeOnly && store !== null) productsList = productsList.filter((p) => p.activeInStore?.[store])
  return productsList.sort(compareProductsByName)
}

export const getProductsListForSearch = (productsDict, suppliersDict={}, productCategoriesDict={}, activeOnly=false, store=null) => {
  let productsList = Object.values(productsDict)
  if (activeOnly && store !== null) productsList = productsList.filter((p) => p.activeInStore?.[store])
  productsList = productsList.sort(compareProductsByName)

  return productsList.map(product => ({
    ...product,
    supplierName: suppliersDict[product.supplierID]?.name || '',
    categoryName: productCategoriesDict[product.categoryID]?.name || ''
  }))
}

export const getProductsByBarcodeDict = (productsDict) => {
  // Null prototype: barcode values are user-entered strings used as keys — a barcode like
  // '__proto__' or 'constructor' on a plain {} would poison the dict / hit inherited keys
  let productsByBarcode = Object.create(null)
  Object.values(productsDict).forEach((product) => {
    // Only index non-empty barcodes to avoid false duplicates. Barcodes are NUMBERS in
    // Firestore (validateProduct writes parseInt) and strings from the Supabase API —
    // normalize before the length check or numeric barcodes silently vanish from the
    // scanner index (ATL-554; `(200098).length` is undefined).
    if (product.barcode && String(product.barcode).length > 0) {
      productsByBarcode[product.barcode] = product
    }
    if (product.barcodes && product.barcodes.length > 0) {
      for (let barcode of product.barcodes) {
        // Skip empty entries; same string/number normalization as above (ATL-554)
        if (barcode && String(barcode).length > 0) {
          productsByBarcode[barcode] = product
        }
      }
    }
  })
  return productsByBarcode
}

export const getProductsByCategoryList = (productsDict, productCategoriesDict, activeOnly=false, store=null) => {
  // Filter inside getProductsList (identical predicate) rather than sorting the whole
  // catalog here and discarding part of it: this is the hottest catalog call site in the
  // app (ProductGrid / AddCartItemScreen) and the sort is O(n log n) since ATL-968.
  let productsList = getProductsList(productsDict, activeOnly, store)
  let productsByCategoryDict = {}
  for (let product of productsList) {
    // ATL-1366: push into the accumulating array rather than copying it TWICE per product, which
    // made the grouping O(products²) per category. That was tolerable while ProductGrid built the
    // catalogue once per session; it no longer is, now that the grid derives it and rebuilds on
    // every products-dict update — i.e. on every realtime stock change, on a trading till.
    // Aliasing is unchanged by this: these arrays are built fresh on every call and the LAST one
    // was always the one handed out (the old copies were purely intermediate), so a caller that
    // receives `data` verbatim — `getIngredientsForProductByCategoryList` with no parent product,
    // or a direct caller of this function — owns exactly what it owned before.
    if (!productsByCategoryDict[product.categoryID]) {
      productsByCategoryDict[product.categoryID] = []
    }
    productsByCategoryDict[product.categoryID].push(product)
  }
  let productsByCategoryList = []
  for (let key in productsByCategoryDict) {
    let newData = {
      id: key,
      title: productCategoriesDict[key]?.name,
      order: productCategoriesDict[key]?.order,
      data: productsByCategoryDict[key]
    }
    productsByCategoryList.push(newData)
  }
  // ATL-1892 — the same total order as compareProductCategories (order, then category id).
  productsByCategoryList.sort((a, b) => compareCategoryFields(a.order, a.id, b.order, b.id))
  return productsByCategoryList
}

export const getProductsByCategoryDict = (productsDict, productCategoriesDict, activeOnly=false, store=null) => {
  // Same as getProductsByCategoryList: let getProductsList apply the activeInStore filter
  // before the sort instead of sorting rows this loop is about to drop.
  let productsList = getProductsList(productsDict, activeOnly, store)
  let productsByCategoryDict = {}
  for (let product of productsList) {
    // Same O(products²)→O(products) grouping fix as getProductsByCategoryList (ATL-1366): this
    // one backs a DERIVED atom (productsByCategoryDictAtom), so it recomputes on every catalogue
    // update too.
    if (!productsByCategoryDict[product.categoryID]) {
      productsByCategoryDict[product.categoryID] = []
    }
    productsByCategoryDict[product.categoryID].push(product)
  }
  return productsByCategoryDict
}

export const getProductsBySupplierDict = (productsDict, activeOnly=false, store=null) => {
  let newProductsBySuppliers = {}
  // Iterate in catalog order (ATL-968) so each supplier's list is alphabetical too.
  // getProductsList applies the exact same activeInStore filter, so pass it through
  // rather than sorting the whole catalog and re-filtering it here.
  for (let value of getProductsList(productsDict, activeOnly, store)) {
    if (!value.isComposite && !value.isService) {
      // Accumulate in place rather than copying the supplier's array per product (ATL-1366):
      // a single-supplier org made this O(products²).
      if (!newProductsBySuppliers[value.supplierID]) {
        newProductsBySuppliers[value.supplierID] = []
      }
      const item = {
        ...value,
        quantity: 0,
        returnQuantity: 0,
        salePrice: value.salePrices?.[store],
        oldPurchasePrice: value.purchasePrice,
        oldSalePrice: value.salePrices?.[store],
        text: value.name,
      }
      newProductsBySuppliers[value.supplierID].push(item)
    }
  }
  return newProductsBySuppliers
}

export const getIngredientsForProductByCategoryList = (parentProduct, productsDict, productCategoriesDict, activeOnly=false, store=null) => {
  let productsByCategoryList = getProductsByCategoryList(productsDict, productCategoriesDict, activeOnly, store)
  if (parentProduct) {
    var filteredData = []
    for (let value of productsByCategoryList) {
      var filteredValue = value.data.filter(function(product) {
        return product.id !== parentProduct.id
      })
      if (filteredValue.length > 0) {
        const newDict = {
          // ATL-1366: carry the category id through. ProductList's category chips key on it and
          // pass it to setChosenCategory; dropping it rendered every chip with key={undefined}
          // and made the chosen-category filter reset on the next products update.
          id: value.id,
          title: value.title,
          data: filteredValue
        }
        filteredData.push(newDict)
      }
    }
    return filteredData
  }
  return productsByCategoryList 
}

export const getCompositeProductsByCategoryList = (productsDict, productCategoriesDict, activeOnly=true) => {
  let productsByCategoryList = getProductsByCategoryList(productsDict, productCategoriesDict, activeOnly, null)
  var filteredData = []
  for (let value of productsByCategoryList) {
    var filteredValue = value.data.filter(function(product) {
      return product.isComposite
    })
    if (filteredValue.length > 0) {
      const newDict = {
        // Same as getIngredientsForProductByCategoryList: keep the category id (ATL-1366).
        id: value.id,
        title: value.title,
        data: filteredValue
      }
      filteredData.push(newDict)
    }
  }
  return filteredData
}

export const getProductsForWriteOffsByCategoryList = (productsDict, productCategoriesDict, activeOnly=true, store=null) => {
  let productsByCategoryList = getProductsByCategoryList(productsDict, productCategoriesDict, activeOnly, store)
  var filteredData = []
  for (let value of productsByCategoryList) {
    var filteredValue = value.data.filter(function(product) {
      return !product.isService
    })
    if (filteredValue.length > 0) {
      const newDict = {
        // Same as getIngredientsForProductByCategoryList: keep the category id (ATL-1366).
        id: value.id,
        title: value.title,
        data: filteredValue
      }
      filteredData.push(newDict)
    }
  }
  return filteredData
}

/**
 * Whether a product may be sold from the New Sale surfaces at all (ATL-1366).
 *
 * `isHiddenInNewSale` is what a merchant sets on an ingredient — flour, concentrated milk,
 * cleaning supplies — so that waiters cannot ring it up as a dish. It is the ONE predicate
 * that decides it, and it has to be applied by every New Sale surface, not just the browse
 * list: Марракеш hid «Молоко концентрированное» and it kept turning up in the picker,
 * because the search box queries the shared all-catalogue Fuse index (productFuseSearchAtom)
 * and never passed through the list this filter builds. 140 of that org's 495 products sit in
 * an all-hidden «Ингредиенты» category that browse correctly drops entirely — search was the
 * only way any of them could be reached, and it reached all of them.
 *
 * Exported so the search paths filter on exactly this, rather than restating it.
 */
export const isVisibleInNewSale = (product) => !product?.isHiddenInNewSale

export const getProductsForNewSaleByCategoryList = (productsDict, productCategoriesDict, includeCustomProduct=false, activeOnly=true, store=null) => {
  let productsByCategoryList = getProductsByCategoryList(productsDict, productCategoriesDict, activeOnly, store)
  var filteredData = []
  for (let value of productsByCategoryList) {
    var filteredValue = value.data.filter(isVisibleInNewSale)
    if (filteredValue.length > 0) {
      const newDict = {
        id: value.id,
        title: value.title,
        data: filteredValue
      }
      filteredData.push(newDict)
    }
  }
  if (filteredData.length > 0 && includeCustomProduct) {
    let dataWithCustomProduct = [{
      id: 'customProduct',
      name: i18n.t('newSale.customProduct'),
      categoryID: 'customProduct',
      supplierID: 'customProduct',
      purchasePrice: 0,
      salePrices: {[store]: 0},
      activeInStore: {[store]: true}
    }, ...filteredData[0].data]
    filteredData[0].data = dataWithCustomProduct
  }
  return filteredData
}

export const getProductsForTransferList = (productsDict, activeOnly=true, store=null) => {
  let productsList = getProductsList(productsDict, activeOnly, store)
  return productsList.filter(function(product) {
    return !product.isService
  })
}
